import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * RetryImage — an <img> that retries failed loads. Behavior wrapper only: no
 * visual opinion beyond the style passed in (not a design-system component).
 *
 * Why (device pass 2, 2026-08-08): a shared photo on a 1-bar connection
 * failed its single load attempt and only appeared minutes later when an
 * app-switch happened to re-render the element. The message pipeline was
 * verified correct (storage object landed before the message) — the gap was
 * the browser giving the file exactly one try.
 *
 * Backoff 2s/4s/8s with a cache-busting query param; gives up after 3
 * attempts until src changes.
 */
const RetryImage = ({ src, alt = '', style, onClick }) => {
  const [attempt, setAttempt] = useState(0);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    attemptRef.current = 0;
    setAttempt(0);
    return () => clearTimeout(timerRef.current);
  }, [src]);

  const handleError = () => {
    if (attemptRef.current >= 3) return;
    clearTimeout(timerRef.current);
    const next = attemptRef.current + 1;
    timerRef.current = setTimeout(
      () => {
        attemptRef.current = next;
        setAttempt(next);
      },
      2000 * 2 ** (next - 1)
    );
  };

  const bustedSrc = attempt > 0 ? `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}` : src;

  return <img src={bustedSrc} alt={alt} style={style} onClick={onClick} onError={handleError} />;
};

RetryImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

export default RetryImage;
